import { INV_CHART } from '../../theme';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Grid, IconButton, Tooltip, TextField, InputAdornment,
  Chip, FormControlLabel, Switch, Autocomplete,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvDataGrid from '../../components/InvDataGrid';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import FilterToolbar from '../../components/FilterToolbar';
import ActiveStatusBadge from '../../components/master/ActiveStatusBadge';
import { invChipSx } from '../../components/StatusChip';
import { InvTextField, InvSelect } from '../../components/InvField';
import { getUoms, createUom, updateUom, deleteUom } from '../../api/inventoryMaster';
import {
  UOM_TYPES, UOM_NAMES_BY_TYPE, UOM_PRESET_DEFAULTS, UOM_PAGE_SIZE, STATUS_FILTER_OPTIONS,
} from '../../constants';
import { normalizeUom, uomRowId, toInventoryPage } from '../../utils/masterEntity';
import { formatDate } from '../../utils';
import { IPD_COLORS, inputSx, formRowSx, switchSx } from '../../theme';
import useMasterList from '../../hooks/useMasterList';

// ─── Module-level constants ────────────────────────────────────────────────────

const EMPTY = {
  uom_code: '',
  uom_name: '',
  uom_type: '',
  decimal_allowed: false,
  description: '',
  is_active: true,
};

const TYPE_CHIP_COLORS = {
  COUNT:  { bg: `${IPD_COLORS.info.text}22`,   color: IPD_COLORS.info.text,      border: `${IPD_COLORS.info.text}55` },
  VOLUME: { bg: `${INV_CHART.purple}22`, color: INV_CHART.purple,    border: `${INV_CHART.purple}55` },
  WEIGHT: { bg: `${IPD_COLORS.warning.text}22`,  color: IPD_COLORS.warning.text, border: `${IPD_COLORS.warning.text}55` },
  LENGTH: { bg: `${IPD_COLORS.primary}22`,   color: IPD_COLORS.primary,      border: `${IPD_COLORS.primary}55` },
  PACK:   { bg: `${IPD_COLORS.textSecondary}22`,  color: IPD_COLORS.textPrimary, border: `${IPD_COLORS.textSecondary}55` },
  OTHER:  { bg: `${IPD_COLORS.textSecondary}22`,  color: IPD_COLORS.textPrimary, border: `${IPD_COLORS.textSecondary}55` },
};

const TYPE_FILTER_OPTIONS = [
  { value: '', label: 'All Types' },
  ...UOM_TYPES.map(t => ({ value: t, label: t })),
];

// ─── Pure sub-components (defined outside to prevent React remount on render) ──

function UomTypeChip({ type }) {
  if (!type) return <span style={{ color: IPD_COLORS.textSecondary }}>—</span>;
  const c = TYPE_CHIP_COLORS[type] || TYPE_CHIP_COLORS.OTHER;
  return <Chip label={type} size="small" sx={invChipSx({ color: c.color, variant: 'soft' })} />;
}

// Active/Inactive is ActiveStatusBadge's job — this file used to carry a verbatim copy.
const StatusBadge = ActiveStatusBadge;

// ─── Param / body builders (pure, module-level) ────────────────────────────────

function buildListParams({ page, search, typeFilter, statusFilter, pageSize }) {
  const params = { limit: pageSize, offset: page * pageSize };
  if (search.trim()) params.search = search.trim();
  if (typeFilter) params.uom_type = typeFilter;
  if (statusFilter === 'active') params.is_active = true;
  if (statusFilter === 'inactive') params.is_active = false;
  return params;
}

function buildCreateBody(data) {
  return {
    uom_code: data.uom_code.trim().toUpperCase(),
    uom_name: data.uom_name.trim(),
    uom_type: data.uom_type,
    decimal_allowed: !!data.decimal_allowed,
    description: data.description?.trim() || '',
  };
}

function buildUpdateBody(data) {
  return {
    uom_name: data.uom_name.trim(),
    uom_type: data.uom_type,
    decimal_allowed: !!data.decimal_allowed,
    description: data.description?.trim() || '',
    is_active: data.is_active !== false,
  };
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function UomList() {
  const list = useMasterList({ pageSize: UOM_PAGE_SIZE });
  const {
    items, setItems, totalCount, setTotalCount,
    loading, setLoading, error, setError,
    page, setPage, searchInput, setSearchInput,
    search, statusFilter, setStatusFilter,
    resetFilters, pageSize,
  } = list;

  const [typeFilter, setTypeFilter] = useState('');
  const [editId, setEditId] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const { control, handleSubmit, reset, setValue, formState: { errors } } = useForm({ defaultValues: EMPTY });
  const watchedType = useWatch({ control, name: 'uom_type' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await getUoms(buildListParams({ page, search, typeFilter, statusFilter, pageSize }));
      const { items: fetched, total } = toInventoryPage(raw, { page, pageSize });
      setItems(fetched.map(normalizeUom));
      setTotalCount(total);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [page, search, typeFilter, statusFilter, pageSize, setLoading, setError, setItems, setTotalCount]);

  useEffect(() => { load(); }, [load]);

  const namePresets = useMemo(
    () => (watchedType ? (UOM_NAMES_BY_TYPE[watchedType] || []) : []),
    [watchedType],
  );

  const startEdit = useCallback((row) => {
    setEditId(uomRowId(row));
    reset({
      uom_code: row.uom_code ?? '',
      uom_name: row.uom_name ?? '',
      uom_type: row.uom_type ?? '',
      decimal_allowed: !!row.decimal_allowed,
      description: row.description ?? '',
      is_active: row.is_active !== false,
    });
  }, [reset]);

  const cancelEdit = useCallback(() => {
    setEditId(null);
    reset(EMPTY);
  }, [reset]);

  function applyNamePreset(name, type) {
    if (!type || !name || editId) return;
    const preset = UOM_PRESET_DEFAULTS[`${type}:${name}`];
    if (preset?.code) setValue('uom_code', preset.code, { shouldValidate: true });
  }

  function handleResetFilters() {
    resetFilters();
    setTypeFilter('');
  }

  const hasActiveFilters = Boolean(search.trim() || typeFilter || statusFilter);

  async function onSubmit(data) {
    setSaving(true);
    try {
      if (editId) {
        await updateUom(editId, buildUpdateBody(data));
        toast.success('UOM updated');
      } else {
        await createUom(buildCreateBody(data));
        toast.success('UOM created');
      }
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e?.message || 'Failed to save UOM');
    } finally {
      setSaving(false);
    }
  }

  async function confirmDeactivate() {
    setDeactivating(true);
    try {
      await deleteUom(uomRowId(deactivateTarget));
      setDeactivateTarget(null);
      toast.success('UOM deactivated');
      load();
    } catch (e) {
      toast.error(e?.message || 'Failed to deactivate UOM');
      setDeactivateTarget(null);
    } finally {
      setDeactivating(false);
    }
  }

  const columns = useMemo(() => [
    { field: 'uom_code', headerName: 'Code', width: 90 },
    { field: 'uom_name', headerName: 'Name', flex: 1, minWidth: 130 },
    {
      field: 'uom_type',
      headerName: 'Type',
      width: 110,
      renderCell: (p) => <UomTypeChip type={p.value} />,
    },
    {
      field: 'decimal_allowed',
      headerName: 'Decimals',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) => p.value
        ? <CheckCircleOutlineIcon sx={{ color: IPD_COLORS.success.text, fontSize: 18 }} />
        : <CancelIcon sx={{ color: IPD_COLORS.textSecondary, fontSize: 18 }} />,
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 110,
      renderCell: (p) => <StatusBadge active={p.value} />,
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 110,
      valueFormatter: (p) => formatDate(p.value),
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => startEdit(p.row)} sx={{ color: IPD_COLORS.primary }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {p.row.is_active !== false && (
            <Tooltip title="Deactivate">
              <IconButton size="small" onClick={() => setDeactivateTarget(p.row)} sx={{ color: IPD_COLORS.error.text }}>
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ], [startEdit]);

  return (
    <Box>
      <PageHeader
        eyebrow="Material Management"
        title="Unit of Measure Master"
        description={`${totalCount || items.length} UOMs`}
      />


      <Grid container spacing={2.5}>
        <Grid item xs={12} md={8}>
          <FilterToolbar>
            <FilterToolbar.Search
              placeholder="Search by code, name or description..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
            <FilterToolbar.Filter
              label="Type"
              value={typeFilter}
              onChange={e => { setTypeFilter(e.target.value); setPage(0); }}
              options={TYPE_FILTER_OPTIONS}
            />
            <FilterToolbar.Filter
              label="Status"
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
              options={STATUS_FILTER_OPTIONS}
            />
            <FilterToolbar.Reset onClick={handleResetFilters} disabled={!hasActiveFilters} />
          </FilterToolbar>

          <InvDataGrid
            rows={items}
            columns={columns}
            loading={loading}
            error={error}
            onRetry={load}
            getRowId={uomRowId}
            emptyMessage="No UOMs yet."
            paginationMode="server"
            rowCount={totalCount}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => setPage(model.page)}
            pageSizeOptions={[pageSize]}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <SectionCard
            title={editId ? 'Edit UOM' : 'Add UOM'}
            actions={editId && (
              <InvButton size="small" variant="outlined" tone="teal" onClick={cancelEdit}>
                Cancel
              </InvButton>
            )}
          >
            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Controller
                    name="uom_code"
                    control={control}
                    rules={{
                      required: 'Required',
                      validate: (v) => {
                        if (editId) return true;
                        const code = v?.trim().toUpperCase();
                        const dup = items.find(u =>
                          u.uom_code?.toUpperCase() === code && uomRowId(u) !== editId,
                        );
                        return dup ? 'Code must be unique' : true;
                      },
                    }}
                    render={({ field }) => (
                      <InvTextField
                        {...field}
                        label="UOM Code *"
                        placeholder="e.g. TAB"
                        error={errors.uom_code}
                        disabled={!!editId}
                        onChange={e => field.onChange(e.target.value.toUpperCase())}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="uom_name"
                    control={control}
                    rules={{ required: 'Required' }}
                    render={({ field }) => (
                      <Autocomplete
                        freeSolo
                        options={namePresets}
                        value={field.value || ''}
                        onChange={(_, v) => {
                          field.onChange(v || '');
                          if (v && namePresets.includes(v)) applyNamePreset(v, watchedType);
                        }}
                        onInputChange={(_, v, reason) => {
                          if (reason === 'input') field.onChange(v);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="UOM Name *"
                            placeholder="e.g. Tablet"
                            size="small"
                            error={!!errors.uom_name}
                            helperText={errors.uom_name?.message}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="uom_type"
                    control={control}
                    rules={{ required: 'Required' }}
                    render={({ field }) => (
                      <InvSelect
                        label="Type *"
                        value={field.value}
                        onChange={e => field.onChange(e.target.value)}
                        options={UOM_TYPES.map(t => ({ value: t, label: t }))}
                        error={errors.uom_type}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="decimal_allowed"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!field.value}
                            onChange={e => field.onChange(e.target.checked)}
                            sx={switchSx}
                          />
                        }
                        label="Allow decimal quantities"
                        sx={{ color: IPD_COLORS.textPrimary }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <InvTextField
                        {...field}
                        label="Description"
                        placeholder="Optional"
                        multiline
                        rows={2}
                      />
                    )}
                  />
                </Grid>
                {editId && (
                  <Grid item xs={12}>
                    <Controller
                      name="is_active"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!field.value}
                              onChange={e => field.onChange(e.target.checked)}
                              sx={switchSx}
                            />
                          }
                          label="Active"
                          sx={{ color: IPD_COLORS.textPrimary }}
                        />
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sx={{ display: 'flex', gap: 1 }}>
                  <InvButton type="submit" loading={saving} startIcon={<AddIcon />} sx={{ flex: 1 }}>
                    {editId ? 'Save Changes' : 'Add UOM'}
                  </InvButton>
                  <InvButton variant="outlined" tone="teal" type="button" onClick={cancelEdit} sx={{ flex: 1 }}>
                    Reset
                  </InvButton>
                </Grid>
              </Grid>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={Boolean(deactivateTarget)}
        title="Deactivate UOM"
        description={`Deactivate "${deactivateTarget?.uom_name}"? It will no longer be available for new transactions.`}
        destructive
        loading={deactivating}
        onConfirm={confirmDeactivate}
        onCancel={() => setDeactivateTarget(null)}
      />
    </Box>
  );
}
